import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertCircle,
  Check,
  CheckCircle,
  Loader2,
  Plus,
  Send,
  Trash2,
  Webhook,
  X,
  XCircle,
} from 'lucide-react'
import type { WebhookResponse } from '@/lib/api'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  useCreateWebhook,
  useDeleteWebhook,
  useTestWebhook,
  useToggleWebhook,
  useUpdateWebhook,
  useWebhookEventTypes,
  useWebhooks,
} from '@/lib/api'

type WebhookFormData = {
  name: string
  url: string
  events: Array<string>
  is_active: boolean
  headers?: Record<string, string> | null
  retry_count: number
  retry_delay_seconds: number
  timeout_seconds: number
  secret?: string | null
}

export function WebhookManagement() {
  const { t } = useTranslation()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingWebhook, setEditingWebhook] = useState<WebhookResponse | null>(
    null,
  )
  const [deletingWebhook, setDeletingWebhook] = useState<number | null>(null)
  const [testingWebhook, setTestingWebhook] = useState<number | null>(null)

  const { data: webhooksData, isLoading, error, refetch } = useWebhooks()
  const {
    data: eventTypesData,
    isLoading: eventTypesLoading,
    error: eventTypesError,
  } = useWebhookEventTypes()
  const createWebhook = useCreateWebhook()
  const updateWebhook = useUpdateWebhook()
  const deleteWebhook = useDeleteWebhook()
  const toggleWebhook = useToggleWebhook()
  const testWebhook = useTestWebhook()

  const webhooks = webhooksData?.webhooks ?? []
  const eventTypes = eventTypesData?.events ?? []

  const handleCreateOrUpdate = async (data: WebhookFormData) => {
    try {
      if (editingWebhook) {
        await updateWebhook.mutateAsync({
          webhookId: editingWebhook.id,
          updates: data,
        })
        toast.success(t('advanced.webhooks.webhookUpdated'))
      } else {
        await createWebhook.mutateAsync(data)
        toast.success(t('advanced.webhooks.webhookCreated'))
      }
      setIsCreateDialogOpen(false)
      setEditingWebhook(null)
    } catch (err) {
      console.error('Error saving webhook:', err)
      toast.error(t('advanced.webhooks.error'))
    }
  }

  const handleDelete = async () => {
    if (!deletingWebhook) return
    try {
      await deleteWebhook.mutateAsync(deletingWebhook)
      toast.success(t('advanced.webhooks.webhookDeleted'))
      setDeletingWebhook(null)
    } catch (err) {
      console.error('Error deleting webhook:', err)
      toast.error(t('advanced.webhooks.error'))
    }
  }

  const handleToggle = async (webhookId: number) => {
    try {
      await toggleWebhook.mutateAsync(webhookId)
      toast.success(t('advanced.webhooks.webhookToggled'))
    } catch (err) {
      console.error('Error toggling webhook:', err)
      toast.error(t('advanced.webhooks.error'))
    }
  }

  const handleTest = async (webhookId: number) => {
    setTestingWebhook(webhookId)
    try {
      const result = await testWebhook.mutateAsync({ webhookId })
      if (result.success) {
        toast.success(
          `${t('advanced.webhooks.testSuccess')} - ${t('advanced.webhooks.httpStatus')}: ${result.status_code} (${result.duration_ms}ms)`,
        )
      } else {
        toast.error(
          `${t('advanced.webhooks.testFailed')} - ${t('advanced.webhooks.httpStatus')}: ${result.status_code} (${result.duration_ms}ms)`,
        )
      }
    } catch (err) {
      console.error('Error testing webhook:', err)
      toast.error(t('advanced.webhooks.testFailed'))
    } finally {
      setTestingWebhook(null)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ms-2 text-muted-foreground">
            {t('advanced.webhooks.loading')}
          </span>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <AlertCircle className="h-8 w-8 text-destructive mb-2" />
          <p className="text-muted-foreground mb-4">
            {t('advanced.webhooks.error')}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            {t('advanced.webhooks.retry')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                {t('advanced.webhooks.title')}
              </CardTitle>
              <CardDescription>
                {t('advanced.webhooks.description')}
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                setEditingWebhook(null)
                setIsCreateDialogOpen(true)
              }}
            >
              <Plus className="h-4 w-4 me-2" />
              {t('advanced.webhooks.createWebhook')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {webhooks.length === 0 ? (
            <div className="text-center py-8">
              <Webhook className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
              <p className="text-muted-foreground font-medium">
                {t('advanced.webhooks.noWebhooks')}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {t('advanced.webhooks.noWebhooksDescription')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map((webhook) => (
                <div
                  key={webhook.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{webhook.name}</h4>
                      <Badge
                        variant={webhook.is_active ? 'default' : 'secondary'}
                      >
                        {webhook.is_active
                          ? t('advanced.webhooks.active')
                          : t('advanced.webhooks.inactive')}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {webhook.url}
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {webhook.events.map((event) => (
                        <Badge
                          key={event}
                          variant="outline"
                          className="text-xs"
                        >
                          {event}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ms-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(webhook.id)}
                      disabled={toggleWebhook.isPending}
                    >
                      {webhook.is_active ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTest(webhook.id)}
                      disabled={testingWebhook === webhook.id}
                    >
                      {testingWebhook === webhook.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingWebhook(webhook)
                        setIsCreateDialogOpen(true)
                      }}
                    >
                      {t('advanced.webhooks.editWebhook')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingWebhook(webhook.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <WebhookDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        webhook={editingWebhook}
        eventTypes={eventTypes}
        eventTypesLoading={eventTypesLoading}
        eventTypesError={eventTypesError}
        onSave={handleCreateOrUpdate}
        isSaving={createWebhook.isPending || updateWebhook.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingWebhook}
        onOpenChange={(open) => !open && setDeletingWebhook(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('advanced.webhooks.confirmDelete')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('advanced.webhooks.confirmDeleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('advanced.webhooks.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteWebhook.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('advanced.webhooks.delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Webhook Create/Edit Dialog Component
function WebhookDialog({
  open,
  onOpenChange,
  webhook,
  eventTypes,
  eventTypesLoading,
  eventTypesError,
  onSave,
  isSaving,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  webhook: WebhookResponse | null
  eventTypes: Array<{ name: string; description: string }>
  eventTypesLoading: boolean
  eventTypesError: Error | null
  onSave: (data: WebhookFormData) => void
  isSaving: boolean
}) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<WebhookFormData>({
    name: webhook?.name ?? '',
    url: webhook?.url ?? '',
    events: webhook?.events ?? [],
    is_active: webhook?.is_active ?? true,
    headers: webhook?.headers ?? {},
    retry_count: webhook?.retry_count ?? 3,
    retry_delay_seconds: webhook?.retry_delay_seconds ?? 5,
    timeout_seconds: webhook?.timeout_seconds ?? 10,
    secret: '', // Secret cannot be read from the API for security reasons
  })
  const [headerKey, setHeaderKey] = useState('')
  const [headerValue, setHeaderValue] = useState('')

  // Reset form when dialog opens/closes or webhook changes
  useEffect(() => {
    if (open) {
      setFormData({
        name: webhook?.name ?? '',
        url: webhook?.url ?? '',
        events: webhook?.events ?? [],
        is_active: webhook?.is_active ?? true,
        headers: webhook?.headers ?? {},
        retry_count: webhook?.retry_count ?? 3,
        retry_delay_seconds: webhook?.retry_delay_seconds ?? 5,
        timeout_seconds: webhook?.timeout_seconds ?? 10,
        secret: '', // Secret cannot be read from the API for security reasons
      })
      setHeaderKey('')
      setHeaderValue('')
    }
  }, [open, webhook])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!formData.name.trim()) {
      toast.warning(t('advanced.webhooks.validation.nameRequired'))
      return
    }
    if (!formData.url.trim()) {
      toast.warning(t('advanced.webhooks.validation.urlRequired'))
      return
    }
    if (formData.events.length === 0) {
      toast.warning(t('advanced.webhooks.validation.eventsRequired'))
      return
    }

    onSave(formData)
  }

  const toggleEventType = (eventType: string) => {
    setFormData((prev) => ({
      ...prev,
      events: prev.events.includes(eventType)
        ? prev.events.filter((e) => e !== eventType)
        : [...prev.events, eventType],
    }))
  }

  const addHeader = () => {
    if (headerKey.trim() && headerValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        headers: {
          ...(prev.headers || {}),
          [headerKey]: headerValue,
        },
      }))
      setHeaderKey('')
      setHeaderValue('')
    }
  }

  const removeHeader = (key: string) => {
    setFormData((prev) => {
      const newHeaders = { ...(prev.headers || {}) }
      delete newHeaders[key]
      return { ...prev, headers: newHeaders }
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {webhook
              ? t('advanced.webhooks.editWebhook')
              : t('advanced.webhooks.createWebhook')}
          </DialogTitle>
          <DialogDescription>
            {t('advanced.webhooks.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t('advanced.webhooks.name')}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder={t('advanced.webhooks.namePlaceholder')}
              required
            />
          </div>

          {/* URL */}
          <div className="space-y-2">
            <Label htmlFor="url">{t('advanced.webhooks.url')}</Label>
            <Input
              id="url"
              type="url"
              value={formData.url}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, url: e.target.value }))
              }
              placeholder={t('advanced.webhooks.urlPlaceholder')}
              required
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_active: checked }))
              }
            />
            <Label htmlFor="is_active">{t('advanced.webhooks.active')}</Label>
          </div>

          {/* Event Types */}
          <div className="space-y-2">
            <Label>{t('advanced.webhooks.selectEvents')}</Label>
            <div className="border rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
              {eventTypesLoading ? (
                <div className="text-center py-4 text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.loading')}...
                </div>
              ) : eventTypesError ? (
                <div className="text-center py-4 text-sm text-destructive">
                  {t('advanced.webhooks.errorLoadingEvents')}
                </div>
              ) : eventTypes.length === 0 ? (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  {t('advanced.webhooks.noEvents')}
                </div>
              ) : (
                eventTypes.map((event) => (
                  <div key={event.name} className="flex items-start gap-2">
                    <Checkbox
                      id={event.name}
                      checked={formData.events.includes(event.name)}
                      onCheckedChange={() => toggleEventType(event.name)}
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={event.name}
                        className="font-medium cursor-pointer"
                        onClick={() => toggleEventType(event.name)}
                      >
                        {event.name}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {event.description}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Custom Headers */}
          <div className="space-y-2">
            <Label>{t('advanced.webhooks.headers')}</Label>
            <div className="space-y-2">
              {Object.entries(formData.headers ?? {}).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <Input value={key} readOnly className="flex-1" />
                  <Input value={value} readOnly className="flex-1" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeHeader(key)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input
                  value={headerKey}
                  onChange={(e) => setHeaderKey(e.target.value)}
                  placeholder={t('advanced.webhooks.headerKey')}
                  className="flex-1"
                />
                <Input
                  value={headerValue}
                  onChange={(e) => setHeaderValue(e.target.value)}
                  placeholder={t('advanced.webhooks.headerValue')}
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={addHeader}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Retry Configuration */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="retry_count">
                {t('advanced.webhooks.maxRetries')}
              </Label>
              <Input
                id="retry_count"
                type="number"
                min="0"
                max="10"
                value={formData.retry_count}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    retry_count: parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="retry_delay">
                {t('advanced.webhooks.retryDelay')}
              </Label>
              <Input
                id="retry_delay"
                type="number"
                min="1"
                max="60"
                value={formData.retry_delay_seconds}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    retry_delay_seconds: parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeout">{t('advanced.webhooks.timeout')}</Label>
              <Input
                id="timeout"
                type="number"
                min="1"
                max="30"
                value={formData.timeout_seconds}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    timeout_seconds: parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
          </div>

          {/* HMAC Secret */}
          <div className="space-y-2">
            <Label htmlFor="secret">{t('advanced.webhooks.hmacSecret')}</Label>
            <Input
              id="secret"
              type="password"
              value={formData.secret ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  secret: e.target.value,
                }))
              }
              placeholder={t('advanced.webhooks.hmacSecretDescription')}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('advanced.webhooks.cancel')}
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 me-2" />
                  {t('advanced.webhooks.save')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
